/**
 * Display helpers for contract NPS (specs/027-contracts-nps).
 *
 * The backend computes every NPS. These helpers only decide how a number
 * looks: its sign, its color and the "Jul 2026" label of a measurement
 * period.
 */

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// U+2212, not a hyphen: it lines up with "+" and doesn't look like a dash.
const MINUS = '−';

/**
 * Theme color of an NPS: green from +50, yellow from 0 to +49, red below 0.
 * @param {number|null|undefined} nps
 * @returns {'success'|'warning'|'error'|null} `null` when there is no NPS.
 */
export function npsColor(nps) {
  if (nps === null || nps === undefined) return null;
  if (nps >= 50) return 'success';
  if (nps >= 0) return 'warning';
  return 'error';
}

/**
 * Formats an NPS with an explicit sign: "+50", "0", "−20".
 * @param {number} nps
 * @returns {string}
 */
export function formatNps(nps) {
  if (nps > 0) return `+${nps}`;
  if (nps < 0) return `${MINUS}${Math.abs(nps)}`;
  return '0';
}

/**
 * Formats a measurement period: "2026-07" → "Jul 2026".
 * @param {string} period `YYYY-MM`.
 * @returns {string}
 */
export function formatPeriod(period) {
  if (!period) return '';
  const [year, month] = period.split('-');
  return `${MONTHS[Number(month) - 1]} ${year}`;
}

/**
 * Current month as a period, in the browser's local time.
 * @param {Date} [now]
 * @returns {string} `YYYY-MM`.
 */
export function currentPeriod(now = new Date()) {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
}

/**
 * Compares two `npsSummary` values for sorting the contracts table.
 *
 * Contracts without NPS ("Sin NPS") always go last, whatever the direction:
 * a descending sort is for finding the happiest clients, not the ones nobody
 * has asked yet.
 * @param {{nps: number}|null} a
 * @param {{nps: number}|null} b
 * @param {boolean} desc
 * @returns {number}
 */
export function compareNpsSummaries(a, b, desc) {
  const hasA = a && typeof a.nps === 'number';
  const hasB = b && typeof b.nps === 'number';
  if (!hasA && !hasB) return 0;
  if (!hasA) return 1;
  if (!hasB) return -1;
  return desc ? b.nps - a.nps : a.nps - b.nps;
}
